"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnassembledPortsError = void 0;
exports.assertQualified = assertQualified;
exports.assertAssembled = assertAssembled;
/** Thrown when a required port reached runtime without a provider. */
class UnassembledPortsError extends Error {
    ports;
    constructor(ports) {
        super(`Unconnected required ports: ${ports.map((p) => `${p.component}.${p.name}`).join(', ')}. ` +
            'Either wire them in the composition root, or add them to the intentionally-defaulted ' +
            'allowlist with a comment saying why.');
        this.ports = ports;
        this.name = 'UnassembledPortsError';
    }
}
exports.UnassembledPortsError = UnassembledPortsError;
/**
 * Reject an allowlist entry that names a port without naming the component it is on.
 *
 * ⚠ THE LISTS KEYED ON `name` ALONE UNTIL 2026-09-09, AND THEY ARE EMPTY, WHICH IS THE ONLY REASON
 * THIS COSTS NOTHING. Both workspaces pass `[]` today, so there was no migration to do — this is a
 * change made ahead of the ~40 ports D1 adds rather than after the first wrong silence.
 *
 * The hazard is not hypothetical: `AuthDomainSecureRandom` and `AuthDomainDigest` are each declared
 * on ComponentDomainAuth and supplied by BOTH ComponentInfrastructureAuth and
 * ComponentInfrastructureLegal, so those names already appear against more than one component. One
 * bare entry would have excused every occurrence, and the one you meant to excuse would be
 * indistinguishable from the one you did not. At 100 names it is a coin flip; at ~140 it is a
 * likelihood.
 *
 * Failing on an unqualified entry rather than treating it as a non-match is deliberate. A non-match
 * would leave the port failing the assertion while its author could see it on the list — "I
 * allowlisted it and it still failed" is a worse minute than this message.
 */
function assertQualified(entries, parameter) {
    const bare = entries.filter((e) => !e.includes('.'));
    if (bare.length > 0) {
        throw new Error(`${parameter} entries must name the component too, as 'Component.Port': ${bare.join(', ')}. ` +
            'A port name alone can match ports on several components — copy the qualified name straight ' +
            'out of the error message this assertion throws.');
    }
}
/**
 * Fail the boot on any required port that nothing connected (rev4 E.6).
 *
 * THIS IS WHAT MAKES NULL DEFAULTS SAFE, and it is the direct answer to rev4 Part B's objection.
 * Explicit ports trade a compile-time guarantee for a runtime one: a missing constructor argument
 * is a TypeScript error, whereas an unconnected `RequiredPortWithDefault` silently degrades to a
 * no-op. §28.29 records what that costs — a registry slot with no registrant is "not a seam, it is
 * a decoy", and finding one took a production-breaking near-miss.
 *
 * With this, a forgotten wire fails the boot instead of quietly no-opping, unless it appears on a
 * list someone had to edit deliberately. The allowlist is the whole safety property: it turns "I
 * forgot" into "I decided", and makes the decision reviewable in a diff.
 *
 * Note it judges `connected`, not `defaulted`. A defaulting port that IS connected is fine — the
 * fallback simply never comes into play. What must not pass unnoticed is a port running on its
 * Null object because nobody supplied the real thing.
 *
 * @param ports          the manifest, from describePorts over the assembled graph
 * @param intentionallyDefaulted QUALIFIED port names — 'Component.Port' — allowed to run unconnected.
 *                       The form matches this function's own error message, so an entry is a copy
 *                       rather than a reconstruction
 */
function assertAssembled(ports, intentionallyDefaulted = []) {
    assertQualified(intentionallyDefaulted, 'intentionallyDefaulted');
    const allowed = new Set(intentionallyDefaulted);
    const unwired = ports.filter((p) => p.kind === 'required' && !p.connected && !allowed.has(`${p.component}.${p.name}`));
    if (unwired.length > 0)
        throw new UnassembledPortsError(unwired);
}
//# sourceMappingURL=assertAssembled.js.map